// src/components/Modals/ModalProgramacion/ModalMatchAdvancedSettings.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import { toast } from "react-hot-toast";
import {
  getMatchParticipantsContext,
  replaceMatchParticipants,
  updateMatchAdvancedSettings,
} from "../../../services/match.js";
import { canReplaceMatchParticipants } from "../../../utils/authUser.js";

const PARTICIPANT_FIELDS = ["idparticipant1", "idparticipant2"];

const toParticipantKey = (value) =>
  value === null || value === undefined ? "" : String(value);

const withoutParticipants = ({ idparticipant1, idparticipant2, ...rest }) => rest;

const getInstitutionLabel = (participant) => {
  const institution = participant?.Institution;

  if (!institution) return "Por definir";

  return (
    institution.abrev ||
    institution.name ||
    `Institución ${institution.idinstitution || ""}`
  );
};

const normalizeText = (value) => value ?? "";

const buildInitialForm = (match) => ({
  idparticipant1: match?.idparticipant1 ?? "",
  idparticipant2: match?.idparticipant2 ?? "",
  idtypephase: String(match?.idtypephase ?? 1),
  nro_fecha: match?.nro_fecha ?? "",
  stateGoleadores: Number(match?.stateGoleadores ?? 0),
  stateSanciones: Number(match?.stateSanciones ?? 0),
  linkLive: normalizeText(match?.linkLive),
  linkPlanilla: normalizeText(match?.linkPlanilla),
  tag_impresion_pdf_final: normalizeText(match?.tag_impresion_pdf_final),
  flag_tag_resaltante: Boolean(match?.flag_tag_resaltante),
  description_match: normalizeText(match?.description_match),
  tag_extra: normalizeText(match?.tag_extra),
  tag_final: normalizeText(match?.tag_final),
});

const isIntegerString = (value) => /^-?\d+$/.test(String(value ?? "").trim());

const ModalMatchAdvancedSettings = ({
  open,
  setOpen,
  match,
  onUpdated,
  onDeleted,
  onParticipantsReplaced,
}) => {
  const [form, setForm] = useState(() => buildInitialForm(match));
  const [saving, setSaving] = useState(false);
  const [participantsContext, setParticipantsContext] = useState(null);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const user = useSelector((state) => state.auth?.user);

  const isOfficialized = Number(match?.state) === 4;
  const canEditMatchData = !isOfficialized;

  // FTR-0039: cambiar el participante 1 o 2 (no solo intercambiarlos) — roles 1 y 4, partido por jugarse.
  // Es solo la pista de interfaz: el backend decide (rol, acceso al evento, estado, BYE, Centro, actividad) y
  // su contexto dice si el partido lo admite y qué participantes son válidos.
  const mayReplaceParticipants =
    canReplaceMatchParticipants(user) && Number(match?.state) === 1;
  const canReplaceParticipants =
    mayReplaceParticipants && participantsContext?.editable !== false;
  const replacementBlockedMessage =
    mayReplaceParticipants && participantsContext?.editable === false
      ? participantsContext.message
      : "";

  const participantLabels = useMemo(() => {
    const p1 = match?.Participant1;
    const p2 = match?.Participant2;

    return {
      [String(match?.idparticipant1 ?? "")]: getInstitutionLabel(p1),
      [String(match?.idparticipant2 ?? "")]: getInstitutionLabel(p2),
    };
  }, [match]);

  useEffect(() => {
    if (open) setForm(buildInitialForm(match));
  }, [open, match]);

  const matchId = match?.idmatch;

  useEffect(() => {
    if (!open || !mayReplaceParticipants || !matchId) {
      return undefined;
    }

    let cancelled = false;
    setLoadingParticipants(true);

    getMatchParticipantsContext(matchId)
      .then((context) => {
        if (!cancelled) setParticipantsContext(context);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          // Sin contexto no se ofrece el cambio: se muestran los participantes actuales como siempre.
          setParticipantsContext({
            editable: false,
            message:
              error?.response?.data?.message ||
              "No se pudieron cargar los participantes válidos de este partido.",
            candidates: [],
            sides: [],
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingParticipants(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, mayReplaceParticipants, matchId]);

  const participantOptions = useMemo(
    () =>
      (participantsContext?.candidates || []).map((candidate) => ({
        value: String(candidate.idparticipant),
        label: candidate.label,
      })),
    [participantsContext]
  );

  const isValidCandidate = (id) =>
    participantOptions.some((option) => option.value === String(id));

  const getParticipantLabel = (id) => {
    if (!id) return "Por definir";
    const side = (participantsContext?.sides || []).find(
      (item) => String(item?.participant?.idparticipant) === String(id)
    );
    return side?.participant?.label || participantLabels[String(id)] || `Participante ${id}`;
  };

  // Lados cuyo participante ya no es el guardado. Solo cuando el par cambia (no un simple intercambio).
  const getParticipantReplacement = () => {
    if (!canReplaceParticipants) return null;

    const pairKey = (ids) =>
      ids.map(toParticipantKey).filter(Boolean).sort().join("|");

    const isSamePair =
      pairKey([form.idparticipant1, form.idparticipant2]) ===
      pairKey([match?.idparticipant1, match?.idparticipant2]);

    if (isSamePair) return null;

    return PARTICIPANT_FIELDS.reduce((changes, field) => {
      if (toParticipantKey(form[field]) !== toParticipantKey(match?.[field])) {
        changes[field] = form[field] === "" ? null : Number(form[field]);
      }

      return changes;
    }, {});
  };

  const hasNonParticipantChanges = () =>
    JSON.stringify(withoutParticipants(form)) !==
    JSON.stringify(withoutParticipants(buildInitialForm(match)));

  const validateParticipantReplacement = (changes) => {
    const p1 = toParticipantKey(form.idparticipant1);

    // Misma regla que el planificador de fases: un participante no juega contra sí mismo.
    if (p1 !== "" && p1 === toParticipantKey(form.idparticipant2)) {
      toast.error("Un participante no puede jugar contra sí mismo.");
      return false;
    }

    const hasInvalidParticipant = Object.values(changes).some(
      (id) => !isValidCandidate(id)
    );

    if (hasInvalidParticipant) {
      toast.error(
        participantsContext?.sameGroupOnly
          ? "Selecciona participantes del grupo de este partido."
          : "Selecciona participantes de la fase de grupos de este deporte."
      );
      return false;
    }

    return true;
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === "nro_fecha") {
      if (value === "" || /^-?\d*$/.test(value)) {
        setForm((prev) => ({ ...prev, [name]: value }));
      }

      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSwapParticipants = () => {
    if (!canEditMatchData) return;

    setForm((prev) => ({
      ...prev,
      idparticipant1: prev.idparticipant2,
      idparticipant2: prev.idparticipant1,
    }));
  };

  const handleUnlockGoleadores = () => {
    setForm((prev) => ({ ...prev, stateGoleadores: 0 }));
  };

  const handleUnlockSanciones = () => {
    setForm((prev) => ({ ...prev, stateSanciones: 0 }));
  };

  const validatePhaseAndDate = () => {
    if (!canEditMatchData) return true;

    const idtypephase = Number(form.idtypephase);

    if (![1, 2].includes(idtypephase)) {
      toast.error(
        "El tipo de fase solo puede ser 1 (Fase de Grupos) o 2 (Fase de Eliminación)."
      );
      return false;
    }

    if (!isIntegerString(form.nro_fecha)) {
      toast.error("El número de fecha debe ser un número entero.");
      return false;
    }

    return true;
  };

  const buildPayload = () => {
    const payload = {
      idmatch: match.idmatch,
    };

    if (
      Number(match?.stateGoleadores ?? 0) === 1 &&
      Number(form.stateGoleadores) === 0
    ) {
      payload.stateGoleadores = 0;
    }

    if (
      Number(match?.stateSanciones ?? 0) === 1 &&
      Number(form.stateSanciones) === 0
    ) {
      payload.stateSanciones = 0;
    }

    if (canEditMatchData) {
      payload.idparticipant1 =
        form.idparticipant1 === "" ? null : Number(form.idparticipant1);

      payload.idparticipant2 =
        form.idparticipant2 === "" ? null : Number(form.idparticipant2);

      payload.idtypephase = Number(form.idtypephase);
      payload.nro_fecha = Number(form.nro_fecha);
      payload.linkLive = form.linkLive;
      payload.linkPlanilla = form.linkPlanilla;
      payload.tag_impresion_pdf_final = form.tag_impresion_pdf_final;
      payload.flag_tag_resaltante = Boolean(form.flag_tag_resaltante);
      payload.description_match = form.description_match;
      payload.tag_extra = form.tag_extra;
      payload.tag_final = form.tag_final;
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validatePhaseAndDate()) return;

    const participantChanges = getParticipantReplacement();

    if (participantChanges && !validateParticipantReplacement(participantChanges)) {
      return;
    }

    let participantsReplaced = false;

    try {
      setSaving(true);

      if (participantChanges) {
        // El backend revalida con el partido bloqueado (rol, estado real, actividad y candidatos); si el
        // listado estaba desactualizado responde el motivo y no escribe nada.
        await replaceMatchParticipants(match.idmatch, participantChanges);
        participantsReplaced = true;
      }

      // Un reemplazo sin otros cambios escribe solo los participantes: el guardado avanzado
      // reescribe todos sus campos (vacíos → null, flag_tag_resaltante → 0).
      // Con otros cambios, el par enviado ya coincide con el guardado en el servidor.
      const saveOtherSettings = !participantsReplaced || hasNonParticipantChanges();

      if (saveOtherSettings) {
        await updateMatchAdvancedSettings(buildPayload());
      }

      const successMessage = !participantsReplaced
        ? "Opciones avanzadas actualizadas."
        : saveOtherSettings
          ? "Participantes y opciones avanzadas actualizados."
          : "Participantes actualizados.";

      toast.success(successMessage);
      await onUpdated?.();
      setOpen(false);
      if (participantsReplaced) onParticipantsReplaced?.();
    } catch (error) {
      console.error(error);

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        "No se pudieron actualizar las opciones avanzadas.";

      if (participantsReplaced) {
        // Los participantes sí quedaron guardados: se refresca y se cierra para no mostrar el par anterior.
        toast.error(`Participantes actualizados, pero no las demás opciones: ${message}`);
        await onUpdated?.();
        setOpen(false);
        onParticipantsReplaced?.();
        return;
      }

      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMatch = async () => {
    if (!canEditMatchData || saving) return;

    const confirmed = window.confirm(
      `¿Seguro que quieres eliminar este partido?\n\nSolo se eliminará si no está oficializado, sus periodos están en cero y no tiene eventos deportivos activos. Los periodos vacíos se borrarán y el match ID ${match?.idmatch} quedará en estado -2.`
    );

    if (!confirmed) return;

    try {
      setSaving(true);

      await updateMatchAdvancedSettings({
        idmatch: match.idmatch,
        state: -2,
      });

      toast.success("Partido eliminado correctamente.");
      await onUpdated?.();
      setOpen(false);
      onDeleted?.();
    } catch (error) {
      console.error(error);

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        "No se pudo eliminar el partido.";

      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const renderParticipantSelect = (field, otherField, label) => {
    const value = toParticipantKey(form[field]);
    const otherValue = toParticipantKey(form[otherField]);

    return (
      <select
        name={field}
        aria-label={label}
        value={value}
        onChange={handleChange}
        disabled={loadingParticipants || saving}
        className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500"
      >
        {value === "" && (
          <option value="" disabled>
            {loadingParticipants ? "Cargando participantes..." : "Selecciona un participante"}
          </option>
        )}

        {value !== "" && !isValidCandidate(value) && (
          <option value={value}>{getParticipantLabel(value)}</option>
        )}

        {participantOptions.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.value === otherValue}
          >
            {option.label}
          </option>
        ))}
      </select>
    );
  };

  return (
    <Modal
      aria-labelledby="modal-match-advanced-title"
      aria-describedby="modal-match-advanced-desc"
      open={open}
      onClose={() => setOpen(false)}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Sheet
        variant="outlined"
        className="w-[min(96vw,920px)] max-h-[88vh] bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col"
      >
        <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b">
          <div className="px-5 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <Typography
                id="modal-match-advanced-title"
                level="h4"
                className="text-xl sm:text-2xl font-semibold"
              >
                Opciones avanzadas del match
              </Typography>

              <p
                id="modal-match-advanced-desc"
                className="text-xs text-gray-500 mt-1"
              >
                ID Match: {match?.idmatch} · Estado: {match?.state ?? "-"}
              </p>
            </div>

            <ModalClose className="m-0" />
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-5 py-4 space-y-5"
        >
          {isOfficialized && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Este partido está oficializado. Solo se permite desbloquear
              goleadores y sanciones.
            </div>
          )}

          <section className="rounded-xl border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h3 className="font-semibold text-gray-800">Participantes</h3>
                <p className="text-xs text-gray-500">
                  Intercambia local y visitante del mismo partido.
                </p>

                {canReplaceParticipants && (
                  <p className="text-xs text-gray-500">
                    Partido por jugarse: también puedes cambiar el participante 1
                    o el participante 2
                    {participantsContext?.sameGroupOnly
                      ? " por otro del mismo grupo."
                      : " por otro participante de este deporte."}{" "}
                    Luego revisa el uniforme de ese lado.
                  </p>
                )}

                {replacementBlockedMessage && (
                  <p className="text-xs text-amber-700">
                    {replacementBlockedMessage}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={handleSwapParticipants}
                disabled={!canEditMatchData || saving}
                className="inline-flex items-center justify-center rounded-full bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Intercambiar participantes
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-lg bg-gray-50 p-3">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Participante 1
                </label>

                {canReplaceParticipants ? (
                  renderParticipantSelect(
                    "idparticipant1",
                    "idparticipant2",
                    "Participante 1"
                  )
                ) : (
                  <div className="mt-1 text-sm font-bold text-gray-900 break-words">
                    {getParticipantLabel(form.idparticipant1)}
                  </div>
                )}

                <div className="mt-1 text-xs text-gray-500">
                  ID: {form.idparticipant1 || "-"}
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-3">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Participante 2
                </label>

                {canReplaceParticipants ? (
                  renderParticipantSelect(
                    "idparticipant2",
                    "idparticipant1",
                    "Participante 2"
                  )
                ) : (
                  <div className="mt-1 text-sm font-bold text-gray-900 break-words">
                    {getParticipantLabel(form.idparticipant2)}
                  </div>
                )}

                <div className="mt-1 text-xs text-gray-500">
                  ID: {form.idparticipant2 || "-"}
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Fase y fecha</h3>

            <p className="text-xs text-gray-500 mb-4">
              Estos campos solo se editan si el partido no está oficializado.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-700">Tipo de fase</label>

                <select
                  name="idtypephase"
                  value={form.idtypephase}
                  onChange={handleChange}
                  disabled={!canEditMatchData || saving}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value="1">1 - Fase de Grupos</option>
                  <option value="2">2 - Fase de Eliminación</option>
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-700">
                  Número de fecha
                </label>

                <input
                  name="nro_fecha"
                  value={form.nro_fecha}
                  onChange={handleChange}
                  disabled={!canEditMatchData || saving}
                  inputMode="numeric"
                  placeholder="Ej. 1"
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Desbloqueos</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      Goleadores
                    </p>

                    <p className="text-xs text-gray-500">
                      Estado actual:{" "}
                      {form.stateGoleadores === 1
                        ? "Bloqueado"
                        : "Desbloqueado"}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleUnlockGoleadores}
                    disabled={form.stateGoleadores !== 1 || saving}
                    className="rounded-full bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Desbloquear
                  </button>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      Sanciones
                    </p>

                    <p className="text-xs text-gray-500">
                      Estado actual:{" "}
                      {form.stateSanciones === 1
                        ? "Bloqueado"
                        : "Desbloqueado"}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleUnlockSanciones}
                    disabled={form.stateSanciones !== 1 || saving}
                    className="rounded-full bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Desbloquear
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <h3 className="font-semibold text-gray-800">
                  Datos adicionales
                </h3>

                <p className="text-xs text-gray-500">
                  Estos campos solo se editan si el partido no está
                  oficializado.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-sm text-gray-700">Link Live</label>

                <input
                  name="linkLive"
                  value={form.linkLive}
                  onChange={handleChange}
                  disabled={!canEditMatchData || saving}
                  placeholder="Vacío = null"
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="text-sm text-gray-700">Link Planilla</label>

                <input
                  name="linkPlanilla"
                  value={form.linkPlanilla}
                  onChange={handleChange}
                  disabled={!canEditMatchData || saving}
                  placeholder="Vacío = null"
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="text-sm text-gray-700">
                  tag_impresion_pdf_final
                </label>

                <input
                  name="tag_impresion_pdf_final"
                  value={form.tag_impresion_pdf_final}
                  onChange={handleChange}
                  disabled={!canEditMatchData || saving}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="text-sm text-gray-700">tag_final</label>

                <input
                  name="tag_final"
                  value={form.tag_final}
                  onChange={handleChange}
                  disabled={!canEditMatchData || saving}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="text-sm text-gray-700">
                  description_match
                </label>

                <input
                  name="description_match"
                  value={form.description_match}
                  onChange={handleChange}
                  disabled={!canEditMatchData || saving}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="text-sm text-gray-700">tag_extra</label>

                <input
                  name="tag_extra"
                  value={form.tag_extra}
                  onChange={handleChange}
                  disabled={!canEditMatchData || saving}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              <label className="sm:col-span-2 inline-flex items-center gap-3 rounded-lg border border-gray-200 p-3">
                <input
                  type="checkbox"
                  name="flag_tag_resaltante"
                  checked={form.flag_tag_resaltante}
                  onChange={handleChange}
                  disabled={!canEditMatchData || saving}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />

                <span className="text-sm font-semibold text-gray-700">
                  Resaltar etiqueta del match
                </span>
              </label>
            </div>
          </section>

          <section className="rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="font-semibold text-red-800">
                  Zona peligrosa
                </h3>

                <p className="text-xs text-red-700">
                  El partido lo marcará con estado eliminado.
                </p>

                {isOfficialized && (
                  <p className="mt-1 text-xs font-semibold text-red-800">
                    No se puede eliminar porque el partido está oficializado.
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={handleDeleteMatch}
                disabled={!canEditMatchData || saving}
                className="inline-flex items-center justify-center rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Eliminar partido
              </button>
            </div>
          </section>

          <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t -mx-5 px-5 py-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={saving}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 disabled:opacity-50"
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </form>
      </Sheet>
    </Modal>
  );
};

export default ModalMatchAdvancedSettings;
